import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { 
  Truck, 
  Package, 
  Users, 
  MessageSquare, 
  BarChart3,
  Search,
  Plus,
  Eye,
  Edit,
  Download,
  Settings,
  ChevronLeft,
  ChevronRight,
  Euro,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  LogOut,
  Upload,
  X,
  Trash2,
  UserPlus,
  RefreshCw,
  Mail,
  Send,
  FileText,
  Receipt,
  Shield,
  Calendar,
  Loader2,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [extendedStats, setExtendedStats] = useState(null);
  const [statsYear, setStatsYear] = useState(new Date().getFullYear());
  const [statsMonth, setStatsMonth] = useState(null); // null = whole year
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [sentInvoices, setSentInvoices] = useState([]);
  
  // Modal states
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(null);
  const [showEditOrder, setShowEditOrder] = useState(null);
  const [showPriceOverride, setShowPriceOverride] = useState(null);
  const [showTicketReply, setShowTicketReply] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(null); // user object
  const [showSendInvoice, setShowSendInvoice] = useState(null); // user object
  const [showStatusInfo, setShowStatusInfo] = useState(null); // order object for status info
  const [statusInfoText, setStatusInfoText] = useState("");
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(null); // order object for details modal
  const [showUserDetails, setShowUserDetails] = useState(null); // user object for details modal
  const [confirmResendLabel, setConfirmResendLabel] = useState(null); // order object for resend confirmation
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null); // user object for delete confirmation
  const [confirmDeleteOrder, setConfirmDeleteOrder] = useState(null); // order object for delete confirmation
  const [showPackageStatus, setShowPackageStatus] = useState(null); // order object for package status management
  const [packageStatusUpdates, setPackageStatusUpdates] = useState({}); // temporary status changes
  
  // Admin invoices state
  const [allInvoices, setAllInvoices] = useState([]);
  const [selectedAdminInvoice, setSelectedAdminInvoice] = useState(null);
  const [invoiceCustomerFilter, setInvoiceCustomerFilter] = useState("");
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(null);
  const [invoiceDetailData, setInvoiceDetailData] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const INVOICES_PER_PAGE = 20;
  
  // Pricing settings state
  const [pricingSettings, setPricingSettings] = useState({
    base_price: 29.90,
    category_prices: {
      STANDARD: 28.00, WAFFEN: 35.00, VALOREN: 45.00,
      FRAGILE: 32.00, DOKUMENTE: 25.00, BLUTPROBEN: 30.00
    },
    express_12_surcharge: 10.00,
    express_10_surcharge: 15.00,
    saturday_surcharge: 20.00,
    volume_surcharge_per_m3: 15.00,
    volume_threshold_m3: 0.1,
    weight_threshold_kg: 10.0,
    weight_surcharge: 5.00,
    weight_step_kg: 5.0,
    weight_step_surcharge: 5.00,
    pickup_morning_surcharge: 5.00,
    pickup_afternoon_surcharge: 5.00,
    austria_enabled: true,
    austria_category_prices: {
      STANDARD: 56.00, WAFFEN: 70.00, VALOREN: 90.00,
      FRAGILE: 64.00, DOKUMENTE: 50.00, BLUTPROBEN: 60.00
    },
    austria_express_12_surcharge: 20.00,
    austria_express_10_surcharge: 30.00,
    austria_saturday_surcharge: 40.00,
    austria_volume_surcharge_per_m3: 30.00,
    austria_weight_surcharge: 10.00,
    austria_weight_step_surcharge: 10.00,
    austria_pickup_morning_surcharge: 10.00,
    austria_pickup_afternoon_surcharge: 10.00
  });
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingCountryTab, setPricingCountryTab] = useState("DE");
  
  // Date filter for orders - default: last 3 days to 2 days in future
  const today = new Date();
  const defaultStartDate = new Date(today);
  defaultStartDate.setDate(today.getDate() - 3);
  const defaultEndDate = new Date(today);
  defaultEndDate.setDate(today.getDate() + 2);
  
  const [dateFilterStart, setDateFilterStart] = useState(defaultStartDate.toISOString().split('T')[0]);
  const [dateFilterEnd, setDateFilterEnd] = useState(defaultEndDate.toISOString().split('T')[0]);
  const [showAllOrders, setShowAllOrders] = useState(false);
  
  // Form states
  const [searchQuery, setSearchQuery] = useState("");
  const [newUserData, setNewUserData] = useState({
    email: "",
    password: "",
    name: "",
    role: "private",
    company_name: "",
    address: "",
    postal_code: "",
    city: "",
    phone: ""
  });
  const [priceOverrideData, setPriceOverrideData] = useState({ price: "", reason: "" });
  const [ticketReplyText, setTicketReplyText] = useState("");
  const [messageData, setMessageData] = useState({ subject: "", content: "", attachment: null });
  const [invoiceData, setInvoiceData] = useState({ month: "", note: "" });
  const [editOrderData, setEditOrderData] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const ORDERS_PER_PAGE = 20;

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, ordersCountRes, usersRes, ticketsRes, sentInvoicesRes, pricingRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`, { headers }),
        axios.get(`${API}/orders?page=${currentPage}&limit=${ORDERS_PER_PAGE}&search=${searchQuery}`, { headers }),
        axios.get(`${API}/orders/count?search=${searchQuery}`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/tickets`, { headers }),
        axios.get(`${API}/sent-invoices`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/pricing`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setOrdersTotal(ordersCountRes.data.total);
      setUsers(usersRes.data);
      setTickets(ticketsRes.data);
      setSentInvoices(sentInvoicesRes.data);
      if (pricingRes.data) {
        setPricingSettings(pricingRes.data);
      }
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  // Refetch orders when page or search changes
  useEffect(() => {
    if (!loading) {
      fetchOrders();
    }
  }, [currentPage, searchQuery]);

  const fetchOrders = async () => {
    try {
      let url = `${API}/orders?page=${currentPage}&limit=${ORDERS_PER_PAGE}&search=${searchQuery}`;
      if (!showAllOrders && dateFilterStart && dateFilterEnd) {
        url += `&date_from=${dateFilterStart}&date_to=${dateFilterEnd}`;
      }
      
      const [ordersRes, ordersCountRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/orders/count?search=${searchQuery}${!showAllOrders ? `&date_from=${dateFilterStart}&date_to=${dateFilterEnd}` : ''}`, { headers })
      ]);
      setOrders(ordersRes.data);
      setOrdersTotal(ordersCountRes.data.total);
    } catch (e) {
      console.error("Error fetching orders:", e);
    }
  };

  const fetchExtendedStats = async () => {
    try {
      const params = new URLSearchParams();
      params.append("year", statsYear);
      if (statsMonth) params.append("month", statsMonth);
      
      const res = await axios.get(`${API}/stats/extended?${params}`, { headers });
      setExtendedStats(res.data);
    } catch (e) {
      console.error("Error fetching extended stats:", e);
    }
  };

  // Fetch extended stats when year/month changes
  useEffect(() => {
    if (activeTab === "statistics") {
      fetchExtendedStats();
    }
  }, [activeTab, statsYear, statsMonth]);

  // Fetch admin invoices when tab changes
  useEffect(() => {
    if (activeTab === "invoices") {
      fetchAdminInvoices();
    }
  }, [activeTab]);

  const fetchAdminInvoices = async () => {
    try {
      const response = await axios.get(`${API}/admin/invoices`, { headers });
      setAllInvoices(response.data);
    } catch (e) {
      console.error("Error fetching invoices:", e);
      toast.error("Fehler beim Laden der Rechnungen");
    }
  };

  // Refetch orders when date filters change
  useEffect(() => {
    if (!loading) {
      setCurrentPage(1);
      fetchOrders();
    }
  }, [dateFilterStart, dateFilterEnd, showAllOrders]);

  const fetchInvoiceDetail = async (customerId, month) => {
    setLoadingInvoiceDetail(true);
    try {
      const response = await axios.get(`${API}/admin/invoices/${customerId}/${month}`, { headers });
      setInvoiceDetailData(response.data);
    } catch (e) {
      toast.error("Fehler beim Laden der Rechnungsdetails");
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  const handleOpenInvoiceDetail = (invoice) => {
    setShowInvoiceDetails(invoice);
    fetchInvoiceDetail(invoice.customer_id, invoice.month);
  };

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API}/users`, newUserData, { headers });
      toast.success("Benutzer erstellt");
      setShowCreateUser(false);
      setNewUserData({ email: "", password: "", name: "", role: "private", company_name: "", address: "", postal_code: "", city: "", phone: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      // Filter out empty strings for optional fields
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([k, v]) => v !== "" || k === "flat_rate")
      );
      await axios.put(`${API}/users/${userId}`, cleanUpdates, { headers });
      toast.success("Benutzer aktualisiert");
      setShowEditUser(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt("Neues Passwort eingeben:");
    if (!newPassword) return;
    
    try {
      await axios.post(`${API}/users/${userId}/reset-password`, { new_password: newPassword }, { headers });
      toast.success("Passwort zurückgesetzt");
    } catch (e) {
      toast.error("Fehler beim Zurücksetzen");
    }
  };

  const handleDeleteUser = async () => {
    if (!confirmDeleteUser) return;
    
    try {
      await axios.delete(`${API}/users/${confirmDeleteUser.id}`, { headers });
      toast.success(`Kunde "${confirmDeleteUser.name}" erfolgreich gelöscht`);
      setConfirmDeleteUser(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleDeleteOrder = async () => {
    if (!confirmDeleteOrder) return;
    
    try {
      await axios.delete(`${API}/orders/${confirmDeleteOrder.id}`, { headers });
      toast.success(`Auftrag ${confirmDeleteOrder.order_number} gelöscht. Kunde wurde benachrichtigt.`);
      setConfirmDeleteOrder(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleUpdatePackageStatus = async (orderId, packageIndex, status, errorType = null, errorInfo = null) => {
    try {
      await axios.put(`${API}/orders/${orderId}/packages/${packageIndex}/status`, {
        package_index: packageIndex,
        status,
        error_type: errorType,
        error_info: errorInfo
      }, { headers });
      toast.success(`Paket ${packageIndex + 1} Status aktualisiert`);
      fetchData();
      // Update local state
      if (showPackageStatus) {
        const updatedOrder = orders.find(o => o.id === orderId);
        if (updatedOrder) {
          setShowPackageStatus({...updatedOrder});
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status }, { headers });
      toast.success("Status aktualisiert");
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handlePriceOverride = async () => {
    if (!showPriceOverride) return;
    
    try {
      await axios.put(`${API}/orders/${showPriceOverride.id}`, {
        price_override: parseFloat(priceOverrideData.price),
        price_override_reason: priceOverrideData.reason
      }, { headers });
      toast.success("Preis überschrieben");
      setShowPriceOverride(null);
      setPriceOverrideData({ price: "", reason: "" });
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Überschreiben");
    }
  };

  const handleTicketReply = async () => {
    if (!showTicketReply) return;
    
    try {
      await axios.post(`${API}/tickets/${showTicketReply.id}/reply`, { message: ticketReplyText }, { headers });
      toast.success("Antwort gesendet");
      setShowTicketReply(null);
      setTicketReplyText("");
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Antworten");
    }
  };

  const handleTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}/status?status=${status}`, {}, { headers });
      toast.success("Status aktualisiert");
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleUploadLabel = async (orderId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const formData = new FormData();
      formData.append("file", file);
      
      try {
        await axios.post(`${API}/orders/${orderId}/label`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        toast.success("Label hochgeladen");
        fetchData();
      } catch (err) {
        toast.error("Fehler beim Hochladen");
      }
    };
    input.click();
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/stats/export`, { headers });
      const blob = new Blob([response.data.csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "orders_export.csv";
      a.click();
      toast.success("Export erfolgreich");
    } catch (e) {
      toast.error("Export fehlgeschlagen");
    }
  };

  const handleSendMessage = async () => {
    if (!showSendMessage || !messageData.subject || !messageData.content) {
      toast.error("Bitte Betreff und Nachricht ausfüllen");
      return;
    }
    
    try {
      // First upload attachment if present
      let attachmentFilename = null;
      if (messageData.attachment) {
        setUploadingAttachment(true);
        const formData = new FormData();
        formData.append("file", messageData.attachment);
        const uploadRes = await axios.post(`${API}/messages/upload-attachment`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        attachmentFilename = uploadRes.data.filename;
        setUploadingAttachment(false);
      }
      
      await axios.post(`${API}/messages`, {
        recipient_id: showSendMessage.id,
        subject: messageData.subject,
        content: messageData.content,
        message_type: "general",
        attachment_file: attachmentFilename
      }, { headers });
      toast.success("Nachricht gesendet");
      setShowSendMessage(null);
      setMessageData({ subject: "", content: "", attachment: null });
    } catch (e) {
      setUploadingAttachment(false);
      toast.error("Fehler beim Senden");
    }
  };

  const handleSendLabelMessage = async (orderId) => {
    try {
      await axios.post(`${API}/messages/send-label?order_id=${orderId}`, {}, { headers });
      toast.success("Label wurde an den Kunden gesendet");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Senden");
    }
  };

  const handleSendInvoice = async () => {
    if (!showSendInvoice || !invoiceData.month) {
      toast.error("Bitte Monat auswählen");
      return;
    }
    
    try {
      await axios.post(`${API}/messages/send-invoice`, {
        user_id: showSendInvoice.id,
        month: invoiceData.month,
        note: invoiceData.note || null
      }, { headers });
      toast.success("Rechnung wurde gesendet");
      setShowSendInvoice(null);
      setInvoiceData({ month: "", note: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Senden");
    }
  };

  const handleEditOrder = async () => {
    if (!editOrderData) return;
    
    try {
      await axios.put(`${API}/orders/${editOrderData.id}`, {
        sender_name: editOrderData.sender_name,
        sender_address: editOrderData.sender_address,
        sender_postal_code: editOrderData.sender_postal_code,
        sender_city: editOrderData.sender_city,
        sender_phone: editOrderData.sender_phone,
        sender_notes: editOrderData.sender_notes,
        receiver_name: editOrderData.receiver_name,
        receiver_address: editOrderData.receiver_address,
        receiver_postal_code: editOrderData.receiver_postal_code,
        receiver_city: editOrderData.receiver_city,
        receiver_phone: editOrderData.receiver_phone,
        receiver_notes: editOrderData.receiver_notes,
        pickup_date: editOrderData.pickup_date,
        delivery_date: editOrderData.delivery_date,
        packages: editOrderData.packages,
        delivery_by_10: editOrderData.delivery_by_10 || false,
        delivery_by_12: editOrderData.delivery_by_12 || false,
        saturday_delivery: editOrderData.saturday_delivery || false
      }, { headers });
      toast.success("Auftrag aktualisiert");
      setShowEditOrder(null);
      setEditOrderData(null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  const openEditOrder = (order) => {
    setEditOrderData({ ...order });
    setShowEditOrder(true);
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!confirm("Ticket wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.")) return;
    
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, { headers });
      setTickets(tickets.filter(t => t.id !== ticketId));
      toast.success("Ticket gelöscht");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleSaveStatusInfo = async () => {
    if (!showStatusInfo) return;
    
    try {
      await axios.put(`${API}/orders/${showStatusInfo.id}/status-info`, {
        status_info: statusInfoText,
        timestamp: new Date().toISOString()
      }, { headers });
      toast.success("Status-Info gespeichert");
      setShowStatusInfo(null);
      setStatusInfoText("");
      fetchOrders();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  // Open status info modal
  useEffect(() => {
    if (showStatusInfo) {
      setStatusInfoText(showStatusInfo.status_info || "");
    }
  }, [showStatusInfo]);

  const isInvoiceSent = (userId, month) => {
    return sentInvoices.some(inv => inv.user_id === userId && inv.month === month);
  };

  // Save pricing settings
  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/settings/pricing`, pricingSettings, { headers });
      toast.success("Preiseinstellungen gespeichert! Gilt für alle zukünftigen Aufträge.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingPricing(false);
    }
  };

  const getAvailableMonths = (userId) => {
    // Get months where user has orders
    const userOrders = orders.filter(o => o.user_id === userId);
    const months = [...new Set(userOrders.map(o => o.created_at?.substring(0, 7)))];
    return months.filter(m => !isInvoiceSent(userId, m)).sort().reverse();
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(ordersTotal / ORDERS_PER_PAGE);

  const openTicketsCount = tickets.filter(t => t.status === "OPEN").length;

  const getStatusBadge = (status) => {
    const statusMap = {
      "PENDING": { label: "Ausstehend", class: "bg-amber-100 text-amber-800" },
      "PICKED_UP": { label: "Abgeholt", class: "bg-blue-100 text-blue-800" },
      "IN_TRANSIT": { label: "In Zustellung", class: "bg-indigo-100 text-indigo-800" },
      "DELIVERED": { label: "Zugestellt", class: "bg-green-100 text-green-800" },
      "CANCELLED": { label: "Storniert", class: "bg-red-100 text-red-800" },
      "CUSTOMER_ABSENT": { label: "Kunde abwesend", class: "bg-red-100 text-red-800" },
      "REDELIVERY": { label: "Erneute Zustellung", class: "bg-orange-100 text-orange-800" }
    };
    const s = statusMap[status] || { label: status, class: "bg-slate-100 text-slate-800" };
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  // Row background color based on status
  const getRowStatusColor = (status) => {
    switch(status) {
      case "DELIVERED":
        return "bg-green-50 hover:bg-green-100 border-l-4 border-l-green-500";
      case "CANCELLED":
      case "CUSTOMER_ABSENT":
        return "bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500";
      case "PICKED_UP":
      case "IN_TRANSIT":
      case "REDELIVERY":
        return "bg-orange-50 hover:bg-orange-100 border-l-4 border-l-orange-500";
      case "PENDING":
      default:
        return "bg-white hover:bg-slate-50 border-l-4 border-l-transparent";
    }
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      "admin": { label: "ADMIN", class: "bg-slate-900 text-white" },
      "support": { label: "SUPPORT", class: "bg-purple-100 text-purple-800" },
      "business": { label: "BUSINESS", class: "bg-blue-100 text-blue-800" },
      "private": { label: "PRIVAT", class: "bg-slate-100 text-slate-800" }
    };
    const r = roleMap[role] || { label: role.toUpperCase(), class: "bg-slate-100 text-slate-800" };
    return <Badge className={r.class}>{r.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-slate-300 hover:text-white transition" data-testid="nav-home">
                Startseite
              </Link>
              <Link to="/admin" className="text-white font-medium" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <Link to="/create-order">
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-order-btn">
                  Sendung erstellen
                </Button>
              </Link>
            </nav>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-medium" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
              <Link to="/account">
                <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" title="Kontoeinstellungen" data-testid="account-settings-btn">
                  <Settings className="h-5 w-5" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400" />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Admin Control Center</h1>
            <p className="text-slate-500">Logistik-Zentrale & Auftragsmanagement</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" />
              CSV Export (Alle)
            </Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setShowSettings(true)} data-testid="settings-btn">
              <Settings className="h-4 w-4 mr-2" />
              Einstellungen
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm" data-testid="stat-orders">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Alle Aufträge</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_orders || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Umsatz Gesamt</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_revenue?.toFixed(2) || "0.00"}€</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Euro className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Offene Anfragen</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.pending_approvals || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-tickets">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Support Tickets</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {openTicketsCount}
                    <span className="text-sm font-normal text-red-500 ml-2">OFFEN</span>
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card className="border-0 shadow-sm">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b px-6 pt-4">
              <TabsList className="bg-transparent gap-6" data-testid="main-tabs">
                <TabsTrigger value="overview" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-overview">
                  Übersicht
                </TabsTrigger>
                <TabsTrigger value="orders" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-orders">
                  Aufträge
                </TabsTrigger>
                <TabsTrigger value="customers" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-customers">
                  Kunden
                </TabsTrigger>
                <TabsTrigger value="invoices" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-invoices">
                  Rechnungen
                </TabsTrigger>
                <TabsTrigger value="support" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-support">
                  Support ({openTicketsCount})
                </TabsTrigger>
                <TabsTrigger value="statistics" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-statistics">
                  Statistiken
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview" className="p-6">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">Sendungen dieser Woche</h3>
                      <p className="text-sm text-slate-500">Basierend auf dem Abholdatum der Aufträge</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Button variant="ghost" size="icon"><ChevronLeft className="h-4 w-4" /></Button>
                      <span className="font-medium">DIESE WOCHE</span>
                      <Button variant="ghost" size="icon"><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats?.weekly_revenue || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip formatter={(value, name) => [value, name === "orders" ? "Sendungen" : "Umsatz €"]} />
                        <Bar dataKey="orders" name="Sendungen" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Letzte Aktivitäten</h3>
                  <div className="space-y-4">
                    {stats?.recent_activity?.map((activity, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`activity-${idx}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Truck className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{activity.order_number}</p>
                            <p className="text-xs text-slate-500">Status: {activity.status}</p>
                            <p className="text-xs text-slate-400">{activity.date}</p>
                          </div>
                        </div>
                        <span className="text-green-600 font-semibold">{activity.price.toFixed(2)}€</span>
                      </div>
                    ))}
                    {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
                      <p className="text-slate-500 text-center py-8">Keine Aktivitäten</p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders" className="p-6">
              {/* Search and Date Filter */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Sendungsnummer, Absender..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    data-testid="order-search"
                  />
                </div>
                
                {/* Date Filter */}
                <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-2">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  <Input
                    type="date"
                    value={dateFilterStart}
                    onChange={(e) => setDateFilterStart(e.target.value)}
                    className="h-8 w-36 text-sm"
                    disabled={showAllOrders}
                    data-testid="date-filter-start"
                  />
                  <span className="text-slate-400">bis</span>
                  <Input
                    type="date"
                    value={dateFilterEnd}
                    onChange={(e) => setDateFilterEnd(e.target.value)}
                    className="h-8 w-36 text-sm"
                    disabled={showAllOrders}
                    data-testid="date-filter-end"
                  />
                  <label className="flex items-center gap-2 ml-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAllOrders}
                      onChange={(e) => setShowAllOrders(e.target.checked)}
                      className="rounded border-slate-300"
                      data-testid="show-all-orders"
                    />
                    <span className="text-sm text-slate-600 whitespace-nowrap">Alle anzeigen</span>
                  </label>
                </div>
                
                <div className="flex items-center gap-4 ml-auto">
                  <span className="text-sm text-slate-500">
                    {ordersTotal} Aufträge {!showAllOrders && "(gefiltert)"}
                  </span>
                  <Link to="/create-order">
                    <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-order-btn-tab">
                      <Plus className="h-4 w-4 mr-2" />
                      Auftrag anlegen
                    </Button>
                  </Link>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="orders-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Nummer</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase pr-6">Abholung</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Abholservice</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase pr-6">Lieferung</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Services</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Absender</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Empfänger</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Preis</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Info</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => (
                      <tr key={order.id} className={`border-b ${getRowStatusColor(order.status)}`} data-testid={`order-row-${order.id}`}>
                        <td className="py-3 font-medium text-sm">{order.order_number}</td>
                        <td className="py-3 pr-6">
                          <span className="font-semibold text-slate-900">
                            {order.pickup_date ? new Date(order.pickup_date).toLocaleDateString('de-DE') : '-'}
                          </span>
                        </td>
                        <td className="py-3">
                          {order.pickup_time_window ? (
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                              order.pickup_time_window === "08:00 - 12:00" 
                                ? "bg-cyan-100 text-cyan-700" 
                                : order.pickup_time_window === "12:00 - 17:00"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-slate-100 text-slate-600"
                            }`}>
                              {order.pickup_time_window}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="py-3 pr-6">
                          <span className="font-semibold text-slate-900">
                            {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('de-DE') : '-'}
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="flex flex-wrap gap-1">
                            {order.delivery_by_10 && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                bis 10 Uhr
                              </span>
                            )}
                            {order.delivery_by_12 && !order.delivery_by_10 && (
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                                bis 12 Uhr
                              </span>
                            )}
                            {order.saturday_delivery && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                                Samstag
                              </span>
                            )}
                            {!order.delivery_by_10 && !order.delivery_by_12 && !order.saturday_delivery && (
                              <span className="text-xs text-slate-400">Standard</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-sm">{order.sender_name}</td>
                        <td className="py-3 text-sm">{order.receiver_name}</td>
                        <td className="py-3 font-medium text-sm">
                          {(order.price_override || order.total_price).toFixed(2)}€
                        </td>
                        <td className="py-3">
                          <Select value={order.status} onValueChange={(v) => handleUpdateOrderStatus(order.id, v)}>
                            <SelectTrigger className="w-40 h-8 text-xs" data-testid={`status-select-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PENDING">Ausstehend</SelectItem>
                              <SelectItem value="PICKED_UP">Abgeholt</SelectItem>
                              <SelectItem value="IN_TRANSIT">In Zustellung</SelectItem>
                              <SelectItem value="REDELIVERY">Erneute Zustellung</SelectItem>
                              <SelectItem value="DELIVERED">Zugestellt</SelectItem>
                              <SelectItem value="CUSTOMER_ABSENT">Kunde abwesend</SelectItem>
                              <SelectItem value="CANCELLED">Storniert</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setShowStatusInfo(order)}
                            className="text-xs h-8 px-2"
                            data-testid={`status-info-${order.id}`}
                            title={order.status_info || "Info hinzufügen"}
                          >
                            {order.status_info ? (
                              <span className="flex items-center gap-1 text-blue-600">
                                <MessageSquare className="h-3 w-3" />
                                Info
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-slate-400">
                                <Plus className="h-3 w-3" />
                                Info
                              </span>
                            )}
                          </Button>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => setShowOrderDetailsModal(order)} data-testid={`view-order-${order.id}`} title="Details anzeigen">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditOrder(order)} data-testid={`edit-order-${order.id}`} title="Auftrag bearbeiten" className="text-blue-600">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setShowPriceOverride(order)} data-testid={`edit-price-${order.id}`} title="Preis überschreiben">
                              <Euro className="h-4 w-4" />
                            </Button>
                            {!order.label_file ? (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleUploadLabel(order.id)} 
                                data-testid={`upload-label-${order.id}`} 
                                title="Label hochladen"
                                className="text-amber-600"
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                            ) : (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => handleUploadLabel(order.id)} 
                                  data-testid={`replace-label-${order.id}`} 
                                  title="Label ersetzen"
                                  className="text-green-600"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => {
                                    // Check if already sent
                                    if (order.label_sent) {
                                      setConfirmResendLabel(order);
                                    } else {
                                      handleSendLabelMessage(order.id);
                                    }
                                  }} 
                                  data-testid={`send-label-${order.id}`} 
                                  title={order.label_sent ? "Label erneut senden" : "Label an Kunden senden"} 
                                  className={order.label_sent ? "text-amber-600" : "text-blue-600"}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setShowPackageStatus(order)} 
                              data-testid={`package-status-${order.id}`} 
                              title="Paket-Status verwalten"
                              className="text-purple-600 hover:text-purple-700"
                            >
                              <Package className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setConfirmDeleteOrder(order)} 
                              data-testid={`delete-order-${order.id}`} 
                              title="Auftrag löschen"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-slate-500">
                          Keine Aufträge gefunden
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Seite {currentPage} von {totalPages} ({ordersTotal} Aufträge)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      data-testid="prev-page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Zurück
                    </Button>
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className={currentPage === pageNum ? "bg-blue-600" : ""}
                          data-testid={`page-${pageNum}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      data-testid="next-page"
                    >
                      Weiter
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Customers Tab */}
            <TabsContent value="customers" className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="relative w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Kunden oder E-Mail suchen..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="customer-search"
                  />
                </div>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setShowCreateUser(true)} data-testid="create-user-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Kunde anlegen
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="customers-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Kunde</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Email / Anschrift</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Typ</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                        <td className="py-4 font-medium">{u.name}</td>
                        <td className="py-4 text-slate-600">{u.email}</td>
                        <td className="py-4">{getRoleBadge(u.role)}</td>
                        <td className="py-4">
                          {u.role !== "admin" && (
                            u.is_active ? (
                              <Badge className="bg-green-100 text-green-800">AKTIV</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800">WARTE</Badge>
                            )
                          )}
                          {u.role === "admin" && <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-4">
                          {u.role !== "admin" && (
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => setShowUserDetails(u)} data-testid={`view-user-${u.id}`} title="Details anzeigen">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setShowEditUser({...u})} data-testid={`edit-user-${u.id}`} title="Bearbeiten">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setShowSendMessage(u)} data-testid={`send-message-${u.id}`} title="Nachricht senden" className="text-blue-600">
                                <Mail className="h-4 w-4" />
                              </Button>
                              {(u.role === "business" || u.payment_method === "invoice") && getAvailableMonths(u.id).length > 0 && (
                                <Button variant="ghost" size="icon" onClick={() => setShowSendInvoice(u)} data-testid={`send-invoice-${u.id}`} title="Rechnung senden" className="text-green-600">
                                  <Receipt className="h-4 w-4" />
                                </Button>
                              )}
                              {u.role !== "admin" && (
                                <Button variant="ghost" size="icon" onClick={() => setConfirmDeleteUser(u)} data-testid={`delete-user-${u.id}`} title="Kunde löschen" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices" className="p-6">
              <div className="mb-4">
                <Input
                  placeholder="Suche nach Kundenname..."
                  value={invoiceCustomerFilter}
                  onChange={(e) => { setInvoiceCustomerFilter(e.target.value); setInvoiceCurrentPage(1); }}
                  className="max-w-md"
                  data-testid="invoice-customer-filter"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-invoices-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Monat</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Kunde</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Firma</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Sendungen</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase text-right">Netto</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase text-right">MwSt. (19%)</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase text-right">Brutto</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(() => {
                      const filtered = allInvoices.filter(inv => 
                        invoiceCustomerFilter === "" || 
                        inv.customer_name.toLowerCase().includes(invoiceCustomerFilter.toLowerCase()) ||
                        (inv.customer_company || "").toLowerCase().includes(invoiceCustomerFilter.toLowerCase())
                      );
                      const startIdx = (invoiceCurrentPage - 1) * INVOICES_PER_PAGE;
                      const paginated = filtered.slice(startIdx, startIdx + INVOICES_PER_PAGE);
                      
                      return paginated.map((invoice, idx) => {
                        const vat = invoice.total * 0.19;
                        const gross = invoice.total + vat;
                        return (
                          <tr 
                            key={`${invoice.customer_id}-${invoice.month}-${idx}`} 
                            className="hover:bg-slate-50 cursor-pointer" 
                            onClick={() => handleOpenInvoiceDetail(invoice)}
                            data-testid={`admin-invoice-${startIdx + idx}`}
                          >
                            <td className="py-3 font-medium text-slate-900">{invoice.month_display}</td>
                            <td className="py-3 text-slate-600">{invoice.customer_name}</td>
                            <td className="py-3 text-slate-600">{invoice.customer_company || "-"}</td>
                            <td className="py-3 text-slate-600">{invoice.order_count}</td>
                            <td className="py-3 text-right text-slate-900">{invoice.total.toFixed(2)}€</td>
                            <td className="py-3 text-right text-slate-500">{vat.toFixed(2)}€</td>
                            <td className="py-3 text-right font-semibold text-slate-900">{gross.toFixed(2)}€</td>
                            <td className="py-3">
                              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                    {allInvoices.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-12 text-center text-slate-500">
                          Keine Rechnungsdaten vorhanden
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {(() => {
                const filtered = allInvoices.filter(inv => 
                  invoiceCustomerFilter === "" || 
                  inv.customer_name.toLowerCase().includes(invoiceCustomerFilter.toLowerCase()) ||
                  (inv.customer_company || "").toLowerCase().includes(invoiceCustomerFilter.toLowerCase())
                );
                const totalPages = Math.ceil(filtered.length / INVOICES_PER_PAGE);
                
                if (totalPages > 1) {
                  return (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-slate-500">
                        Seite {invoiceCurrentPage} von {totalPages} ({filtered.length} Rechnungen)
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={invoiceCurrentPage === 1}
                          onClick={() => setInvoiceCurrentPage(p => p - 1)}
                        >
                          Zurück
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={invoiceCurrentPage === totalPages}
                          onClick={() => setInvoiceCurrentPage(p => p + 1)}
                        >
                          Weiter
                        </Button>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Summary */}
              {allInvoices.length > 0 && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-sm text-slate-500">Gesamt Rechnungen</p>
                      <p className="text-2xl font-bold text-slate-900">{allInvoices.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Gesamt Sendungen</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {allInvoices.reduce((sum, inv) => sum + inv.order_count, 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Netto Gesamt</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {allInvoices.reduce((sum, inv) => sum + inv.total, 0).toFixed(2)}€
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Brutto Gesamt (inkl. 19% MwSt.)</p>
                      <p className="text-2xl font-bold text-green-600">
                        {(allInvoices.reduce((sum, inv) => sum + inv.total, 0) * 1.19).toFixed(2)}€
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Support Tab */}
            <TabsContent value="support" className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="tickets-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Datum</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Kunde</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Betreff</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Paket-ID</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="pb-3 text-xs font-medium text-slate-500 uppercase">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map(ticket => (
                      <tr key={ticket.id} className="border-b hover:bg-slate-50" data-testid={`ticket-row-${ticket.id}`}>
                        <td className="py-4">{ticket.created_at?.split("T")[0]}</td>
                        <td className="py-4 font-medium">{ticket.user_name || "Unbekannt"}</td>
                        <td className="py-4">{ticket.subject}</td>
                        <td className="py-4 text-slate-600">{ticket.order_number || "Allgemein"}</td>
                        <td className="py-4">
                          {ticket.status === "OPEN" ? (
                            <Badge className="bg-red-100 text-red-800">OPEN</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">RESOLVED</Badge>
                          )}
                        </td>
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <Button 
                              className="bg-blue-600 hover:bg-blue-700" 
                              size="sm"
                              onClick={() => setShowTicketReply(ticket)}
                              data-testid={`reply-ticket-${ticket.id}`}
                            >
                              Beantworten
                            </Button>
                            {user?.role === "admin" && (
                              <Button 
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteTicket(ticket.id)}
                                data-testid={`delete-ticket-${ticket.id}`}
                                title="Ticket löschen"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tickets.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-500">
                          Keine Tickets vorhanden
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Statistics Tab */}
            <TabsContent value="statistics" className="p-6">
              {/* Period Selector */}
              <div className="flex items-center gap-4 mb-6 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Jahr:</Label>
                  <Select value={String(statsYear)} onValueChange={(v) => { setStatsYear(parseInt(v)); setStatsMonth(null); }}>
                    <SelectTrigger className="w-28" data-testid="stats-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(extendedStats?.available_years || [new Date().getFullYear()]).map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Monat:</Label>
                  <Select value={statsMonth ? String(statsMonth) : "all"} onValueChange={(v) => setStatsMonth(v === "all" ? null : parseInt(v))}>
                    <SelectTrigger className="w-36" data-testid="stats-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Gesamtes Jahr</SelectItem>
                      <SelectItem value="1">Januar</SelectItem>
                      <SelectItem value="2">Februar</SelectItem>
                      <SelectItem value="3">März</SelectItem>
                      <SelectItem value="4">April</SelectItem>
                      <SelectItem value="5">Mai</SelectItem>
                      <SelectItem value="6">Juni</SelectItem>
                      <SelectItem value="7">Juli</SelectItem>
                      <SelectItem value="8">August</SelectItem>
                      <SelectItem value="9">September</SelectItem>
                      <SelectItem value="10">Oktober</SelectItem>
                      <SelectItem value="11">November</SelectItem>
                      <SelectItem value="12">Dezember</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={fetchExtendedStats} data-testid="refresh-stats">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Aktualisieren
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="border shadow-sm bg-gradient-to-br from-green-50 to-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      Umsatz
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-700">{extendedStats?.summary?.total_revenue?.toFixed(2) || 0}€</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {statsMonth ? `${extendedStats?.period?.month_name} ${statsYear}` : `Jahr ${statsYear}`}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border shadow-sm bg-gradient-to-br from-blue-50 to-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <Package className="h-4 w-4 text-blue-600" />
                      Sendungen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-blue-700">{extendedStats?.summary?.total_orders || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">{extendedStats?.summary?.total_packages || 0} Pakete</p>
                  </CardContent>
                </Card>

                <Card className="border shadow-sm bg-gradient-to-br from-purple-50 to-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <Euro className="h-4 w-4 text-purple-600" />
                      Ø Auftragswert
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-purple-700">{extendedStats?.summary?.avg_order_value?.toFixed(2) || 0}€</p>
                    <p className="text-xs text-slate-400 mt-1">Durchschnittlich</p>
                  </CardContent>
                </Card>

                <Card className="border shadow-sm bg-gradient-to-br from-amber-50 to-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <Users className="h-4 w-4 text-amber-600" />
                      Neukunden
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-amber-700">{extendedStats?.summary?.new_customers || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">Im Zeitraum</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid lg:grid-cols-2 gap-6 mb-8">
                {/* Revenue Chart */}
                <Card className="border shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-green-600" />
                      {statsMonth ? "Täglicher Umsatz" : "Monatlicher Umsatz"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={extendedStats?.chart_data || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip 
                            formatter={(value) => [`${value.toFixed(2)}€`, 'Umsatz']}
                            contentStyle={{ fontSize: 12 }}
                          />
                          <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Orders Chart */}
                <Card className="border shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Package className="h-5 w-5 text-blue-600" />
                      {statsMonth ? "Tägliche Sendungen" : "Monatliche Sendungen"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={extendedStats?.chart_data || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip 
                            formatter={(value) => [value, 'Sendungen']}
                            contentStyle={{ fontSize: 12 }}
                          />
                          <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Details Row */}
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Status Distribution */}
                <Card className="border shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Status-Verteilung</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.entries(extendedStats?.status_distribution || {}).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="text-sm">
                          {status === "PENDING" ? "Ausstehend" :
                           status === "PICKED_UP" ? "Abgeholt" :
                           status === "IN_TRANSIT" ? "In Zustellung" :
                           status === "DELIVERED" ? "Zugestellt" :
                           status === "CANCELLED" ? "Storniert" :
                           status === "CUSTOMER_ABSENT" ? "Kunde abwesend" :
                           status === "REDELIVERY" ? "Erneute Zustellung" : status}
                        </span>
                        <span className="font-medium text-sm">{count}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Delivery Options */}
                <Card className="border shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Lieferoptionen</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Express bis 10 Uhr</span>
                      <span className="font-medium">{extendedStats?.delivery_options?.express_10 || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Express bis 12 Uhr</span>
                      <span className="font-medium">{extendedStats?.delivery_options?.express_12 || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Samstagszustellung</span>
                      <span className="font-medium">{extendedStats?.delivery_options?.saturday || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Standard</span>
                      <span className="font-medium">{extendedStats?.delivery_options?.standard || 0}</span>
                    </div>
                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between text-green-700">
                        <span className="text-sm font-medium">Zuschläge Umsatz</span>
                        <span className="font-bold">{extendedStats?.summary?.surcharges_revenue?.toFixed(2) || 0}€</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Customers */}
                <Card className="border shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Top Kunden</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-52 overflow-y-auto">
                      {(extendedStats?.top_customers || []).map((customer, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1 border-b last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center text-xs font-medium">
                              {idx + 1}
                            </span>
                            <span className="text-sm truncate max-w-[120px]" title={customer.name}>
                              {customer.name}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-sm text-green-700">{customer.revenue.toFixed(2)}€</p>
                            <p className="text-xs text-slate-400">{customer.orders} Aufträge</p>
                          </div>
                        </div>
                      ))}
                      {(!extendedStats?.top_customers || extendedStats.top_customers.length === 0) && (
                        <p className="text-sm text-slate-400 text-center py-4">Keine Daten</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </main>

      {/* Create User Modal */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-modal">
          <DialogHeader>
            <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rolle</Label>
              <Select value={newUserData.role} onValueChange={(v) => setNewUserData({ ...newUserData, role: v })}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Privatkunde</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input 
                value={newUserData.name} 
                onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>E-Mail</Label>
              <Input 
                type="email"
                value={newUserData.email} 
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Passwort</Label>
              <Input 
                type="password"
                value={newUserData.password} 
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                data-testid="new-user-password"
              />
            </div>
            {newUserData.role === "business" && (
              <div className="space-y-2">
                <Label>Firmenname</Label>
                <Input 
                  value={newUserData.company_name} 
                  onChange={(e) => setNewUserData({ ...newUserData, company_name: e.target.value })}
                  data-testid="new-user-company"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Abbrechen</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateUser} data-testid="save-new-user">
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={!!showEditUser} onOpenChange={() => setShowEditUser(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten: {showEditUser?.name}</DialogTitle>
          </DialogHeader>
          {showEditUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input 
                    value={showEditUser.name || ""} 
                    onChange={(e) => setShowEditUser({ ...showEditUser, name: e.target.value })}
                    data-testid="edit-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>E-Mail *</Label>
                  <Input 
                    type="email"
                    value={showEditUser.email || ""} 
                    onChange={(e) => setShowEditUser({ ...showEditUser, email: e.target.value })}
                    data-testid="edit-user-email"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Neues Passwort (leer lassen = unverändert)</Label>
                <Input 
                  type="password"
                  placeholder="••••••••"
                  value={showEditUser.newPassword || ""} 
                  onChange={(e) => setShowEditUser({ ...showEditUser, newPassword: e.target.value })}
                  data-testid="edit-user-password"
                />
              </div>
              
              {showEditUser.role === "business" && (
                <div className="space-y-2">
                  <Label>Firmenname</Label>
                  <Input 
                    value={showEditUser.company_name || ""} 
                    onChange={(e) => setShowEditUser({ ...showEditUser, company_name: e.target.value })}
                    data-testid="edit-user-company"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input 
                  value={showEditUser.address || ""} 
                  onChange={(e) => setShowEditUser({ ...showEditUser, address: e.target.value })}
                  placeholder="Straße und Hausnummer"
                  data-testid="edit-user-address"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PLZ</Label>
                  <Input 
                    value={showEditUser.postal_code || ""} 
                    onChange={(e) => setShowEditUser({ ...showEditUser, postal_code: e.target.value })}
                    data-testid="edit-user-postal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stadt</Label>
                  <Input 
                    value={showEditUser.city || ""} 
                    onChange={(e) => setShowEditUser({ ...showEditUser, city: e.target.value })}
                    data-testid="edit-user-city"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input 
                  value={showEditUser.phone || ""} 
                  onChange={(e) => setShowEditUser({ ...showEditUser, phone: e.target.value })}
                  data-testid="edit-user-phone"
                />
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Kontoeinstellungen</h4>
                
                <div className="flex items-center justify-between mb-4">
                  <Label>Aktiv</Label>
                  <Switch 
                    checked={showEditUser.is_active} 
                    onCheckedChange={(v) => setShowEditUser({ ...showEditUser, is_active: v })}
                    data-testid="edit-user-active"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Rolle</Label>
                    <Select 
                      value={showEditUser.role} 
                      onValueChange={(v) => setShowEditUser({ ...showEditUser, role: v })}
                    >
                      <SelectTrigger data-testid="edit-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Privatkunde</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="support">Support</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Zahlungsart</Label>
                    <Select 
                      value={showEditUser.payment_method} 
                      onValueChange={(v) => setShowEditUser({ ...showEditUser, payment_method: v })}
                    >
                      <SelectTrigger data-testid="edit-user-payment">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Sofortzahlung</SelectItem>
                        <SelectItem value="invoice">Rechnung</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2 mt-4">
                  <Label>Pauschalpreis (€) - Legacy</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={showEditUser.flat_rate || ""} 
                    onChange={(e) => setShowEditUser({ ...showEditUser, flat_rate: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="Kein Pauschalpreis"
                    data-testid="edit-user-flatrate"
                  />
                  <p className="text-xs text-slate-500">Alt: Gilt für alle Kategorien wenn keine Kategoriepreise gesetzt sind.</p>
                </div>

                {/* Category-specific flat rates */}
                {(showEditUser.role === "business" || showEditUser.role === "private") && (
                  <div className="space-y-4 mt-6 pt-4 border-t">
                    <h4 className="font-medium text-slate-900 flex items-center gap-2">
                      <Euro className="h-4 w-4 text-green-600" />
                      Kategorie-Pauschalpreise
                    </h4>
                    <p className="text-xs text-slate-500">
                      Individuelle Pauschalpreise pro Kategorie für diesen Kunden. Leere Felder = Standardpreis.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: "STANDARD", label: "Standard" },
                        { key: "WAFFEN", label: "Waffen" },
                        { key: "VALOREN", label: "Valoren" },
                        { key: "FRAGILE", label: "Zerbrechlich" },
                        { key: "DOKUMENTE", label: "Dokumente" },
                        { key: "BLUTPROBEN", label: "Blutproben" }
                      ].map(cat => (
                        <div key={cat.key} className="flex items-center gap-2">
                          <Label className="text-sm w-24">{cat.label}</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            className="w-24"
                            value={showEditUser.category_flat_rates?.[cat.key] || ""} 
                            onChange={(e) => setShowEditUser({ 
                              ...showEditUser, 
                              category_flat_rates: {
                                ...showEditUser.category_flat_rates,
                                [cat.key]: e.target.value ? parseFloat(e.target.value) : null
                              }
                            })}
                            placeholder="Standard"
                            data-testid={`edit-user-cat-${cat.key.toLowerCase()}`}
                          />
                          <span className="text-xs text-slate-400">€</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Support Permissions */}
                {showEditUser.role === "support" && (
                  <div className="space-y-4 mt-6 pt-4 border-t">
                    <h4 className="font-medium text-slate-900 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-purple-600" />
                      Support-Berechtigungen
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.can_edit_orders || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, can_edit_orders: v }
                          })}
                        />
                        <span className="text-sm">Aufträge bearbeiten</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.can_change_status || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, can_change_status: v }
                          })}
                        />
                        <span className="text-sm">Status ändern</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.can_send_messages || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, can_send_messages: v }
                          })}
                        />
                        <span className="text-sm">Nachrichten senden</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.can_manage_tickets || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, can_manage_tickets: v }
                          })}
                        />
                        <span className="text-sm">Tickets verwalten</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.can_view_stats || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, can_view_stats: v }
                          })}
                        />
                        <span className="text-sm">Statistiken anzeigen</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.can_edit_customers || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, can_edit_customers: v }
                          })}
                        />
                        <span className="text-sm">Kunden bearbeiten</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 border-amber-200 bg-amber-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.edit_customer_prices || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, edit_customer_prices: v }
                          })}
                        />
                        <span className="text-sm">Kundenpreise bearbeiten</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 border-amber-200 bg-amber-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.edit_pricing || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, edit_pricing: v }
                          })}
                        />
                        <span className="text-sm">Preiseinstellungen</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 border-red-200 bg-red-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.delete_customers || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, delete_customers: v }
                          })}
                        />
                        <span className="text-sm">Kunden löschen</span>
                      </label>
                      <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 border-red-200 bg-red-50">
                        <Checkbox 
                          checked={showEditUser.permissions?.delete_orders || false}
                          onCheckedChange={(v) => setShowEditUser({ 
                            ...showEditUser, 
                            permissions: { ...showEditUser.permissions, delete_orders: v }
                          })}
                        />
                        <span className="text-sm">Aufträge löschen</span>
                      </label>
                    </div>
                    <p className="text-xs text-slate-500">
                      Wählen Sie die Berechtigungen für diesen Support-Mitarbeiter aus.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUser(null)}>Abbrechen</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={() => handleUpdateUser(showEditUser.id, {
                name: showEditUser.name,
                email: showEditUser.email,
                password: showEditUser.newPassword || undefined,
                company_name: showEditUser.company_name,
                address: showEditUser.address,
                postal_code: showEditUser.postal_code,
                city: showEditUser.city,
                phone: showEditUser.phone,
                is_active: showEditUser.is_active,
                role: showEditUser.role,
                payment_method: showEditUser.payment_method,
                flat_rate: showEditUser.flat_rate,
                category_flat_rates: showEditUser.category_flat_rates,
                permissions: showEditUser.permissions
              })}
              data-testid="save-edit-user"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Override Modal */}
      <Dialog open={!!showPriceOverride} onOpenChange={() => setShowPriceOverride(null)}>
        <DialogContent className="sm:max-w-md" data-testid="price-override-modal">
          <DialogHeader>
            <DialogTitle>Preis überschreiben</DialogTitle>
          </DialogHeader>
          {showPriceOverride && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Auftrag: {showPriceOverride.order_number}<br />
                Aktueller Preis: {showPriceOverride.total_price.toFixed(2)}€
              </p>
              <div className="space-y-2">
                <Label>Neuer Preis (€)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={priceOverrideData.price} 
                  onChange={(e) => setPriceOverrideData({ ...priceOverrideData, price: e.target.value })}
                  data-testid="override-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Begründung</Label>
                <Textarea 
                  value={priceOverrideData.reason} 
                  onChange={(e) => setPriceOverrideData({ ...priceOverrideData, reason: e.target.value })}
                  placeholder="Grund für die Preisänderung..."
                  data-testid="override-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceOverride(null)}>Abbrechen</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handlePriceOverride} data-testid="save-price-override">
              Überschreiben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Reply Modal */}
      <Dialog open={!!showTicketReply} onOpenChange={() => setShowTicketReply(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="ticket-reply-modal">
          <DialogHeader>
            <DialogTitle>Ticket beantworten</DialogTitle>
          </DialogHeader>
          {showTicketReply && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium">{showTicketReply.subject}</p>
                <p className="text-sm text-slate-500 mt-1">Von: {showTicketReply.user_name}</p>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-3">
                {showTicketReply.messages?.map((msg, idx) => (
                  <div key={idx} className="p-3 bg-white border rounded-lg">
                    <p className="text-sm font-medium">{msg.user_name}</p>
                    <p className="text-sm text-slate-600 mt-1">{msg.message}</p>
                    <p className="text-xs text-slate-400 mt-2">{msg.timestamp?.split("T")[0]}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Label>Ihre Antwort</Label>
                <Textarea 
                  value={ticketReplyText} 
                  onChange={(e) => setTicketReplyText(e.target.value)}
                  placeholder="Antwort eingeben..."
                  rows={4}
                  data-testid="ticket-reply-text"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => handleTicketStatus(showTicketReply.id, showTicketReply.status === "OPEN" ? "RESOLVED" : "OPEN")}
                  data-testid="toggle-ticket-status"
                >
                  {showTicketReply.status === "OPEN" ? "Als gelöst markieren" : "Wieder öffnen"}
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketReply(null)}>Abbrechen</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleTicketReply} data-testid="send-ticket-reply">
              Antworten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="settings-modal">
          <DialogHeader>
            <DialogTitle>Versandeinstellungen</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Hinweis:</strong> Preisänderungen gelten nur für <strong>zukünftige Aufträge</strong>. 
                Bestehende Aufträge behalten ihre ursprünglichen Preise.
              </p>
            </div>

            {/* Country Tabs */}
            <div className="flex gap-2 border-b pb-0">
              <button
                onClick={() => setPricingCountryTab("DE")}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
                  pricingCountryTab === "DE"
                    ? "bg-white text-blue-700 border-blue-300"
                    : "bg-slate-100 text-slate-500 hover:text-slate-700 border-transparent"
                }`}
                data-testid="pricing-tab-de"
              >
                Deutschland (DE)
              </button>
              <button
                onClick={() => setPricingCountryTab("AT")}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
                  pricingCountryTab === "AT"
                    ? "bg-white text-blue-700 border-blue-300"
                    : "bg-slate-100 text-slate-500 hover:text-slate-700 border-transparent"
                }`}
                data-testid="pricing-tab-at"
              >
                Österreich (AT)
              </button>
            </div>
            
            {/* Pricing Fields - DE or AT */}
            {(() => {
              const isAT = pricingCountryTab === "AT";
              const prefix = isAT ? "austria_" : "";
              const catKey = isAT ? "austria_category_prices" : "category_prices";
              const getVal = (field) => pricingSettings[prefix + field] ?? pricingSettings[field] ?? 0;
              const setVal = (field, val) => setPricingSettings({...pricingSettings, [prefix + field]: val});
              const getCat = (cat) => (pricingSettings[catKey] || {})[cat] || 0;
              const setCat = (cat, val) => setPricingSettings({
                ...pricingSettings,
                [catKey]: { ...(pricingSettings[catKey] || {}), [cat]: val }
              });

              return (
                <div className="grid gap-4">
                  {/* Category Prices Section */}
                  <div className="border-b pb-4">
                    <h4 className="font-semibold text-slate-900 mb-3">
                      Kategoriepreise {isAT ? "(Österreich)" : "(Deutschland)"}
                    </h4>
                    <div className="grid gap-3">
                      {[
                        { key: "STANDARD", label: "Standard" },
                        { key: "WAFFEN", label: "Jagd & Sportwaffen" },
                        { key: "VALOREN", label: "Valoren (Wertgegenstände)" },
                        { key: "FRAGILE", label: "Zerbrechlich" },
                        { key: "DOKUMENTE", label: "Dokumente" },
                        { key: "BLUTPROBEN", label: "Blutproben / Med. Proben" }
                      ].map(cat => (
                        <div key={cat.key} className="grid grid-cols-2 gap-4 items-center">
                          <Label className="text-sm">{cat.label}</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number" step="0.01" min="0"
                              value={getCat(cat.key)}
                              onChange={(e) => setCat(cat.key, parseFloat(e.target.value) || 0)}
                              className="w-28"
                              data-testid={`pricing-cat-${cat.key.toLowerCase()}${isAT ? "-at" : ""}`}
                            />
                            <span className="text-slate-500">€</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Legacy base_price hidden */}
                  {!isAT && <input type="hidden" value={pricingSettings.base_price}
                    onChange={(e) => setPricingSettings({...pricingSettings, base_price: parseFloat(e.target.value) || 0})} />}

                  {/* Express 12 Uhr */}
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Zuschlag Express bis 12 Uhr</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.01" min="0"
                        value={getVal("express_12_surcharge")}
                        onChange={(e) => setVal("express_12_surcharge", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-express12${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>

                  {/* Express 10 Uhr */}
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Zuschlag Express bis 10 Uhr</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.01" min="0"
                        value={getVal("express_10_surcharge")}
                        onChange={(e) => setVal("express_10_surcharge", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-express10${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>

                  {/* Saturday */}
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Zuschlag Samstagszustellung</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.01" min="0"
                        value={getVal("saturday_surcharge")}
                        onChange={(e) => setVal("saturday_surcharge", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-saturday${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>

                  <hr className="my-2" />

                  {/* Volume Surcharge */}
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Volumenpreis pro m³</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.01" min="0"
                        value={getVal("volume_surcharge_per_m3")}
                        onChange={(e) => setVal("volume_surcharge_per_m3", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-volume${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€/m³</span>
                    </div>
                  </div>

                  {/* Volume Threshold (shared) */}
                  {!isAT && (
                    <div className="grid grid-cols-2 gap-4 items-center">
                      <Label className="font-medium">Volumen-Schwelle (ab wann)</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">&gt;</span>
                        <Input type="number" step="0.01" min="0"
                          value={pricingSettings.volume_threshold_m3}
                          onChange={(e) => setPricingSettings({...pricingSettings, volume_threshold_m3: parseFloat(e.target.value) || 0})}
                          className="w-28" data-testid="pricing-volume-threshold" />
                        <span className="text-slate-500">m³</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          
          {/* Weight-based Pricing */}
          <div className="border-t pt-4">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-slate-600" />
              Gewichtszuschläge {pricingCountryTab === "AT" ? "(Österreich)" : "(Deutschland)"} (kumulativ)
            </h4>
            {(() => {
              const isAT = pricingCountryTab === "AT";
              const prefix = isAT ? "austria_" : "";
              const getVal = (field) => pricingSettings[prefix + field] ?? pricingSettings[field] ?? 0;
              const setVal = (field, val) => setPricingSettings({...pricingSettings, [prefix + field]: val});

              return (
                <div className="space-y-4">
                  {/* Weight Threshold (shared) */}
                  {!isAT && (
                    <div className="grid grid-cols-2 gap-4 items-center">
                      <Label className="font-medium">Ab Gewicht (kg)</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">&gt;</span>
                        <Input type="number" step="0.1" min="0"
                          value={pricingSettings.weight_threshold_kg}
                          onChange={(e) => setPricingSettings({...pricingSettings, weight_threshold_kg: parseFloat(e.target.value) || 0})}
                          className="w-28" data-testid="pricing-weight-threshold" />
                        <span className="text-slate-500">kg</span>
                      </div>
                    </div>
                  )}

                  {/* Weight Surcharge */}
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Erster Zuschlag</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.5" min="0"
                        value={getVal("weight_surcharge")}
                        onChange={(e) => setVal("weight_surcharge", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-weight-surcharge${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>

                  {/* Weight Step (shared) */}
                  {!isAT && (
                    <div className="grid grid-cols-2 gap-4 items-center">
                      <Label className="font-medium">Schrittweite (weitere kg)</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">je</span>
                        <Input type="number" step="0.5" min="0.1"
                          value={pricingSettings.weight_step_kg}
                          onChange={(e) => setPricingSettings({...pricingSettings, weight_step_kg: parseFloat(e.target.value) || 0.1})}
                          className="w-28" data-testid="pricing-weight-step" />
                        <span className="text-slate-500">kg</span>
                      </div>
                    </div>
                  )}

                  {/* Weight Step Surcharge */}
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Zuschlag pro Schritt</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.5" min="0"
                        value={getVal("weight_step_surcharge")}
                        onChange={(e) => setVal("weight_step_surcharge", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-weight-step-surcharge${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>

                  {/* Example calculation */}
                  {(() => {
                    const ws = getVal("weight_surcharge");
                    const wss = getVal("weight_step_surcharge");
                    const wt = pricingSettings.weight_threshold_kg || 10;
                    const wsk = pricingSettings.weight_step_kg || 5;
                    return (
                      <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                        <p className="font-medium mb-1">Beispiel {isAT ? "(Österreich)" : "(Deutschland)"}:</p>
                        <p>Ab {wt}kg: +{ws}€</p>
                        <p>Ab {wt + wsk}kg: +{ws + wss}€</p>
                        <p>Ab {wt + (2 * wsk)}kg: +{ws + (2 * wss)}€</p>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
          
          {/* Pickup Time Window Surcharges */}
          <div className="border-t pt-4">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-600" />
              Zeitfenster-Zuschläge {pricingCountryTab === "AT" ? "(Österreich)" : "(Deutschland)"}
            </h4>
            {(() => {
              const isAT = pricingCountryTab === "AT";
              const prefix = isAT ? "austria_" : "";
              const getVal = (field) => pricingSettings[prefix + field] ?? pricingSettings[field] ?? 0;
              const setVal = (field, val) => setPricingSettings({...pricingSettings, [prefix + field]: val});

              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Abholung 8-12 Uhr</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.5" min="0"
                        value={getVal("pickup_morning_surcharge")}
                        onChange={(e) => setVal("pickup_morning_surcharge", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-pickup-morning${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <Label className="font-medium">Abholung 12-17 Uhr</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">+</span>
                      <Input type="number" step="0.5" min="0"
                        value={getVal("pickup_afternoon_surcharge")}
                        onChange={(e) => setVal("pickup_afternoon_surcharge", parseFloat(e.target.value) || 0)}
                        className="w-28" data-testid={`pricing-pickup-afternoon${isAT ? "-at" : ""}`} />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSettings(false)}>Abbrechen</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleSavePricing}
              disabled={savingPricing}
              data-testid="save-pricing-btn"
            >
              {savingPricing ? "Speichern..." : "Preise speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message Modal */}
      <Dialog open={!!showSendMessage} onOpenChange={() => { setShowSendMessage(null); setMessageData({ subject: "", content: "", attachment: null }); }}>
        <DialogContent className="sm:max-w-lg" data-testid="send-message-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Nachricht an {showSendMessage?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p><strong>Empfänger:</strong> {showSendMessage?.name}</p>
              <p><strong>E-Mail:</strong> {showSendMessage?.email}</p>
            </div>
            <div className="space-y-2">
              <Label>Betreff *</Label>
              <Input 
                value={messageData.subject}
                onChange={(e) => setMessageData({ ...messageData, subject: e.target.value })}
                placeholder="Betreff der Nachricht"
                data-testid="message-subject"
              />
            </div>
            <div className="space-y-2">
              <Label>Nachricht *</Label>
              <Textarea 
                value={messageData.content}
                onChange={(e) => setMessageData({ ...messageData, content: e.target.value })}
                placeholder="Ihre Nachricht..."
                rows={6}
                data-testid="message-content"
              />
            </div>
            <div className="space-y-2">
              <Label>PDF-Anhang (optional)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setMessageData({ ...messageData, attachment: e.target.files?.[0] || null })}
                  className="flex-1"
                  data-testid="message-attachment"
                />
                {messageData.attachment && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setMessageData({ ...messageData, attachment: null })}
                    className="text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {messageData.attachment && (
                <p className="text-xs text-slate-500">
                  Ausgewählt: {messageData.attachment.name}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSendMessage(null); setMessageData({ subject: "", content: "", attachment: null }); }}>
              Abbrechen
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleSendMessage} 
              disabled={uploadingAttachment}
              data-testid="send-message-btn"
            >
              {uploadingAttachment ? (
                <>Hochladen...</>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Senden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Invoice Modal */}
      <Dialog open={!!showSendInvoice} onOpenChange={() => { setShowSendInvoice(null); setInvoiceData({ month: "", note: "" }); }}>
        <DialogContent className="sm:max-w-md" data-testid="send-invoice-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              Rechnung senden an {showSendInvoice?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-green-50 p-3 rounded-lg text-sm">
              <p><strong>Kunde:</strong> {showSendInvoice?.company_name || showSendInvoice?.name}</p>
              <p><strong>E-Mail:</strong> {showSendInvoice?.email}</p>
              <p><strong>Zahlungsart:</strong> {showSendInvoice?.payment_method === "invoice" ? "Rechnung" : "Sofortzahlung"}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Abrechnungsmonat *</Label>
              <Select 
                value={invoiceData.month} 
                onValueChange={(v) => setInvoiceData({ ...invoiceData, month: v })}
              >
                <SelectTrigger data-testid="invoice-month">
                  <SelectValue placeholder="Monat auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {showSendInvoice && getAvailableMonths(showSendInvoice.id).map(month => {
                    const [year, m] = month.split("-");
                    const monthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni", 
                                       "Juli", "August", "September", "Oktober", "November", "Dezember"];
                    return (
                      <SelectItem key={month} value={month}>
                        {monthNames[parseInt(m) - 1]} {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Nur Monate mit Aufträgen werden angezeigt. Bereits gesendete Rechnungen werden ausgeblendet.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Notiz (optional)</Label>
              <Textarea 
                value={invoiceData.note}
                onChange={(e) => setInvoiceData({ ...invoiceData, note: e.target.value })}
                placeholder="Optionale Notiz zur Rechnung..."
                rows={3}
                data-testid="invoice-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSendInvoice(null); setInvoiceData({ month: "", note: "" }); }}>
              Abbrechen
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleSendInvoice} data-testid="send-invoice-btn">
              <Send className="h-4 w-4 mr-2" />
              Rechnung senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Modal */}
      <Dialog open={showEditOrder} onOpenChange={() => { setShowEditOrder(false); setEditOrderData(null); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              Auftrag bearbeiten: {editOrderData?.order_number}
            </DialogTitle>
          </DialogHeader>
          {editOrderData && (
            <div className="space-y-6">
              {/* Sender Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">ABSENDER</span>
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name / Firma</Label>
                    <Input 
                      value={editOrderData.sender_name || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, sender_name: e.target.value })}
                      data-testid="edit-order-sender-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefon</Label>
                    <Input 
                      value={editOrderData.sender_phone || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, sender_phone: e.target.value })}
                      data-testid="edit-order-sender-phone"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Adresse</Label>
                    <Input 
                      value={editOrderData.sender_address || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, sender_address: e.target.value })}
                      data-testid="edit-order-sender-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PLZ</Label>
                    <Input 
                      value={editOrderData.sender_postal_code || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, sender_postal_code: e.target.value })}
                      data-testid="edit-order-sender-postal"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stadt</Label>
                    <Input 
                      value={editOrderData.sender_city || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, sender_city: e.target.value })}
                      data-testid="edit-order-sender-city"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Notizen</Label>
                    <Textarea 
                      value={editOrderData.sender_notes || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, sender_notes: e.target.value })}
                      data-testid="edit-order-sender-notes"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Receiver Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-indigo-900 mb-4 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs">EMPFÄNGER</span>
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name / Firma</Label>
                    <Input 
                      value={editOrderData.receiver_name || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, receiver_name: e.target.value })}
                      data-testid="edit-order-receiver-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefon</Label>
                    <Input 
                      value={editOrderData.receiver_phone || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, receiver_phone: e.target.value })}
                      data-testid="edit-order-receiver-phone"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Adresse</Label>
                    <Input 
                      value={editOrderData.receiver_address || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, receiver_address: e.target.value })}
                      data-testid="edit-order-receiver-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PLZ</Label>
                    <Input 
                      value={editOrderData.receiver_postal_code || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, receiver_postal_code: e.target.value })}
                      data-testid="edit-order-receiver-postal"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stadt</Label>
                    <Input 
                      value={editOrderData.receiver_city || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, receiver_city: e.target.value })}
                      data-testid="edit-order-receiver-city"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Notizen</Label>
                    <Textarea 
                      value={editOrderData.receiver_notes || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, receiver_notes: e.target.value })}
                      data-testid="edit-order-receiver-notes"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Dates Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-4">Termine</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Abholdatum</Label>
                    <Input 
                      type="date"
                      value={editOrderData.pickup_date || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, pickup_date: e.target.value })}
                      data-testid="edit-order-pickup-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zustelldatum</Label>
                    <Input 
                      type="date"
                      value={editOrderData.delivery_date || ""} 
                      onChange={(e) => setEditOrderData({ ...editOrderData, delivery_date: e.target.value })}
                      data-testid="edit-order-delivery-date"
                    />
                  </div>
                </div>
              </div>

              {/* Services Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Express-Services
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                    editOrderData.delivery_by_10 ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-red-300"
                  }`}>
                    <input
                      type="checkbox"
                      checked={editOrderData.delivery_by_10 || false}
                      onChange={(e) => setEditOrderData({ 
                        ...editOrderData, 
                        delivery_by_10: e.target.checked,
                        delivery_by_12: e.target.checked ? false : editOrderData.delivery_by_12
                      })}
                      className="w-4 h-4"
                      data-testid="edit-order-express10"
                    />
                    <div>
                      <p className="font-medium text-red-700">Express bis 10:00</p>
                      <p className="text-xs text-slate-500">+15,00€</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                    editOrderData.delivery_by_12 && !editOrderData.delivery_by_10 ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-orange-300"
                  }`}>
                    <input
                      type="checkbox"
                      checked={editOrderData.delivery_by_12 || false}
                      onChange={(e) => setEditOrderData({ 
                        ...editOrderData, 
                        delivery_by_12: e.target.checked,
                        delivery_by_10: e.target.checked ? false : editOrderData.delivery_by_10
                      })}
                      className="w-4 h-4"
                      data-testid="edit-order-express12"
                    />
                    <div>
                      <p className="font-medium text-orange-700">Express bis 12:00</p>
                      <p className="text-xs text-slate-500">+10,00€</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                    editOrderData.saturday_delivery ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-purple-300"
                  }`}>
                    <input
                      type="checkbox"
                      checked={editOrderData.saturday_delivery || false}
                      onChange={(e) => setEditOrderData({ ...editOrderData, saturday_delivery: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="edit-order-saturday"
                    />
                    <div>
                      <p className="font-medium text-purple-700">Samstagszustellung</p>
                      <p className="text-xs text-slate-500">+20,00€</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Packages Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-4">Packstücke ({editOrderData.packages?.length || 0})</h4>
                <div className="space-y-3">
                  {editOrderData.packages?.map((pkg, idx) => (
                    <div key={pkg.id || idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <span className="font-bold text-slate-400">{idx + 1}</span>
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs">Kategorie</Label>
                          <Select 
                            value={pkg.category || "STANDARD"} 
                            onValueChange={(v) => {
                              const newPkgs = [...editOrderData.packages];
                              newPkgs[idx] = { ...newPkgs[idx], category: v };
                              setEditOrderData({ ...editOrderData, packages: newPkgs });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="STANDARD">Standard</SelectItem>
                              <SelectItem value="VALOREN">Valoren</SelectItem>
                              <SelectItem value="FRAGILE">Zerbrechlich</SelectItem>
                              <SelectItem value="DOKUMENTE">Dokumente</SelectItem>
                              <SelectItem value="WAFFEN">Jagd & Sportwaffen</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Gewicht (kg)</Label>
                          <Input 
                            type="number"
                            step="0.1"
                            value={pkg.weight || 1}
                            onChange={(e) => {
                              const newPkgs = [...editOrderData.packages];
                              newPkgs[idx] = { ...newPkgs[idx], weight: parseFloat(e.target.value) || 1 };
                              setEditOrderData({ ...editOrderData, packages: newPkgs });
                            }}
                          />
                        </div>
                      </div>
                      {(pkg.length && pkg.width && pkg.height) && (
                        <div className="ml-8 text-xs text-slate-500">
                          Maße: {pkg.length} × {pkg.width} × {pkg.height} cm
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditOrder(false); setEditOrderData(null); }}>
              Abbrechen
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleEditOrder} data-testid="save-edit-order">
              Änderungen speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Info Modal */}
      <Dialog open={!!showStatusInfo} onOpenChange={() => { setShowStatusInfo(null); setStatusInfoText(""); }}>
        <DialogContent className="sm:max-w-md" data-testid="status-info-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Status-Info: {showStatusInfo?.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {showStatusInfo?.status_info_history && showStatusInfo.status_info_history.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-slate-500 mb-2">Verlauf:</p>
                {showStatusInfo.status_info_history.map((entry, idx) => (
                  <div key={idx} className="text-xs border-l-2 border-slate-300 pl-2 mb-2">
                    <span className="text-slate-400">{new Date(entry.timestamp).toLocaleString('de-DE')}</span>
                    <p className="text-slate-700">{entry.text}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Neue Status-Information</Label>
              <Textarea
                value={statusInfoText}
                onChange={(e) => setStatusInfoText(e.target.value)}
                placeholder="z.B. Kunde wurde telefonisch informiert, Paket wird morgen nochmal zugestellt..."
                rows={3}
                data-testid="status-info-text"
              />
              <p className="text-xs text-slate-500">
                Diese Info wird mit Zeitstempel gespeichert und ist intern sichtbar.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowStatusInfo(null); setStatusInfoText(""); }}>
              Abbrechen
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveStatusInfo} data-testid="save-status-info">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Details Modal (Popup) */}
      <Dialog open={!!showOrderDetailsModal} onOpenChange={() => setShowOrderDetailsModal(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="order-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Auftragsdetails: {showOrderDetailsModal?.order_number}
            </DialogTitle>
          </DialogHeader>
          {showOrderDetailsModal && (
            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <Badge className={`px-3 py-1 ${
                  showOrderDetailsModal.status === "DELIVERED" ? "bg-green-100 text-green-800" :
                  showOrderDetailsModal.status === "IN_TRANSIT" ? "bg-blue-100 text-blue-800" :
                  showOrderDetailsModal.status === "PICKED_UP" ? "bg-indigo-100 text-indigo-800" :
                  showOrderDetailsModal.status === "CANCELLED" ? "bg-red-100 text-red-800" :
                  showOrderDetailsModal.status === "CUSTOMER_ABSENT" ? "bg-red-100 text-red-800" :
                  showOrderDetailsModal.status === "REDELIVERY" ? "bg-orange-100 text-orange-800" :
                  "bg-amber-100 text-amber-800"
                }`}>
                  {showOrderDetailsModal.status === "PENDING" ? "Ausstehend" :
                   showOrderDetailsModal.status === "PICKED_UP" ? "Abgeholt" :
                   showOrderDetailsModal.status === "IN_TRANSIT" ? "In Zustellung" :
                   showOrderDetailsModal.status === "DELIVERED" ? "Zugestellt" :
                   showOrderDetailsModal.status === "CUSTOMER_ABSENT" ? "Kunde abwesend" :
                   showOrderDetailsModal.status === "REDELIVERY" ? "Erneute Zustellung" :
                   showOrderDetailsModal.status === "CANCELLED" ? "Storniert" : showOrderDetailsModal.status}
                </Badge>
                <span className="text-sm text-slate-500">
                  Erstellt: {new Date(showOrderDetailsModal.created_at).toLocaleDateString('de-DE')}
                </span>
                <span className="font-semibold text-slate-900 ml-auto">
                  {(showOrderDetailsModal.price_override || showOrderDetailsModal.total_price)?.toFixed(2)}€
                </span>
              </div>

              {/* Sender/Receiver Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">A</span>
                    Absender
                  </h4>
                  <p className="font-semibold">{showOrderDetailsModal.sender_name}</p>
                  <p className="text-sm text-slate-600">{showOrderDetailsModal.sender_address}</p>
                  <p className="text-sm text-slate-600">{showOrderDetailsModal.sender_postal_code} {showOrderDetailsModal.sender_city}</p>
                  {showOrderDetailsModal.sender_phone && <p className="text-sm text-blue-600 mt-2">{showOrderDetailsModal.sender_phone}</p>}
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <h4 className="font-medium text-indigo-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs">E</span>
                    Empfänger
                  </h4>
                  <p className="font-semibold">{showOrderDetailsModal.receiver_name}</p>
                  <p className="text-sm text-slate-600">{showOrderDetailsModal.receiver_address}</p>
                  <p className="text-sm text-slate-600">{showOrderDetailsModal.receiver_postal_code} {showOrderDetailsModal.receiver_city}</p>
                  {showOrderDetailsModal.receiver_phone && <p className="text-sm text-indigo-600 mt-2">{showOrderDetailsModal.receiver_phone}</p>}
                </div>
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <span className="text-xs text-slate-500">Abholung</span>
                  <p className="font-semibold">{showOrderDetailsModal.pickup_date ? new Date(showOrderDetailsModal.pickup_date).toLocaleDateString('de-DE') : '-'}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Lieferung</span>
                  <p className="font-semibold">{showOrderDetailsModal.delivery_date ? new Date(showOrderDetailsModal.delivery_date).toLocaleDateString('de-DE') : '-'}</p>
                </div>
              </div>

              {/* Gebuchte Services */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-600" />
                  Gebuchte Services
                </h4>
                <div className="flex flex-wrap gap-2">
                  {showOrderDetailsModal.delivery_by_10 && (
                    <Badge className="bg-red-100 text-red-700 px-3 py-1">
                      Express bis 10:00 Uhr
                    </Badge>
                  )}
                  {showOrderDetailsModal.delivery_by_12 && !showOrderDetailsModal.delivery_by_10 && (
                    <Badge className="bg-orange-100 text-orange-700 px-3 py-1">
                      Express bis 12:00 Uhr
                    </Badge>
                  )}
                  {showOrderDetailsModal.saturday_delivery && (
                    <Badge className="bg-purple-100 text-purple-700 px-3 py-1">
                      Samstagszustellung
                    </Badge>
                  )}
                  {!showOrderDetailsModal.delivery_by_10 && !showOrderDetailsModal.delivery_by_12 && !showOrderDetailsModal.saturday_delivery && (
                    <span className="text-sm text-slate-500">Keine Zusatzservices gebucht (Standardlieferung)</span>
                  )}
                </div>
              </div>

              {/* Packages */}
              <div>
                <h4 className="font-medium mb-2">Packstücke ({showOrderDetailsModal.packages?.length || 0})</h4>
                <div className="space-y-2">
                  {showOrderDetailsModal.packages?.map((pkg, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">#{idx + 1} - {pkg.category || 'STANDARD'}</span>
                        <span className="text-sm font-medium">{pkg.weight} kg</span>
                      </div>
                      {(pkg.length && pkg.width && pkg.height) && (
                        <p className="text-xs text-slate-500">
                          Maße: {pkg.length} × {pkg.width} × {pkg.height} cm
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* History */}
              {showOrderDetailsModal.history && showOrderDetailsModal.history.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Sendungsverlauf</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {showOrderDetailsModal.history.slice().reverse().map((event, idx) => (
                      <div key={idx} className={`p-2 rounded text-sm ${
                        event.type === "info" ? "bg-amber-50 border-l-4 border-amber-400" :
                        event.event?.includes("zugestellt") ? "bg-green-50 border-l-4 border-green-400" :
                        "bg-slate-50 border-l-4 border-slate-300"
                      }`}>
                        <p className="font-medium">{event.event?.replace("INFO: ", "")}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(event.timestamp).toLocaleString('de-DE')} {event.user && `• ${event.user}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDetailsModal(null)}>Schließen</Button>
            <Button onClick={() => { navigate(`/order/${showOrderDetailsModal.id}`); setShowOrderDetailsModal(null); }}>
              Vollansicht öffnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Modal */}
      <Dialog open={!!showUserDetails} onOpenChange={() => setShowUserDetails(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="user-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Kundendetails
            </DialogTitle>
          </DialogHeader>
          {showUserDetails && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-2xl font-bold text-slate-400">
                  {showUserDetails.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xl font-semibold">{showUserDetails.name}</p>
                  <Badge className={`${
                    showUserDetails.role === "business" ? "bg-green-100 text-green-800" :
                    showUserDetails.role === "support" ? "bg-blue-100 text-blue-800" :
                    "bg-slate-100 text-slate-800"
                  }`}>
                    {showUserDetails.role?.toUpperCase()}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">E-Mail</p>
                  <p className="font-medium">{showUserDetails.email}</p>
                </div>
                {showUserDetails.phone && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Telefon</p>
                    <p className="font-medium">{showUserDetails.phone}</p>
                  </div>
                )}
                {showUserDetails.company_name && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 uppercase">Firma</p>
                    <p className="font-medium">{showUserDetails.company_name}</p>
                  </div>
                )}
                {showUserDetails.address && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 uppercase">Adresse</p>
                    <p className="font-medium">{showUserDetails.address}</p>
                    <p className="font-medium">{showUserDetails.postal_code} {showUserDetails.city}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 uppercase">Zahlungsart</p>
                  <p className="font-medium">{showUserDetails.payment_method === "invoice" ? "Rechnung" : "Sofortzahlung"}</p>
                </div>
                {showUserDetails.flat_rate && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pauschalpreis</p>
                    <p className="font-medium text-green-600">{showUserDetails.flat_rate}€</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 uppercase">Status</p>
                  <Badge className={showUserDetails.is_active ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
                    {showUserDetails.is_active ? "AKTIV" : "WARTEND"}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Registriert</p>
                  <p className="font-medium">{showUserDetails.created_at ? new Date(showUserDetails.created_at).toLocaleDateString('de-DE') : '-'}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDetails(null)}>Schließen</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setShowEditUser({...showUserDetails}); setShowUserDetails(null); }}>
              Bearbeiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Label Confirmation Modal */}
      <Dialog open={!!confirmResendLabel} onOpenChange={() => setConfirmResendLabel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="resend-label-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Label erneut senden?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Das Label für Auftrag <strong>{confirmResendLabel?.order_number}</strong> wurde bereits gesendet.
            </p>
            <p className="text-slate-600 mt-2">
              Möchten Sie es erneut an den Kunden senden?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmResendLabel(null)}>Abbrechen</Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600" 
              onClick={() => {
                handleSendLabelMessage(confirmResendLabel.id);
                setConfirmResendLabel(null);
              }}
              data-testid="confirm-resend-label"
            >
              Ja, erneut senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!confirmDeleteUser} onOpenChange={() => setConfirmDeleteUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-delete-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Kunde löschen
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Sind Sie sicher, dass Sie den Kunden <strong>"{confirmDeleteUser?.name}"</strong> löschen möchten?
            </p>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Hinweis:</strong> Die Aufträge des Kunden bleiben erhalten und können weiterhin eingesehen werden.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteUser(null)}>Abbrechen</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={handleDeleteUser}
              data-testid="confirm-delete-user"
            >
              Ja, löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirmation Dialog */}
      <Dialog open={!!confirmDeleteOrder} onOpenChange={() => setConfirmDeleteOrder(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-delete-order-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Auftrag löschen
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Sind Sie sicher, dass Sie den Auftrag <strong>{confirmDeleteOrder?.order_number}</strong> löschen möchten?
            </p>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Info:</strong> Der Kunde wird automatisch über die Löschung benachrichtigt.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOrder(null)}>Abbrechen</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={handleDeleteOrder}
              data-testid="confirm-delete-order"
            >
              Ja, löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package Status Management Modal */}
      <Dialog open={!!showPackageStatus} onOpenChange={() => setShowPackageStatus(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="package-status-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />
              Paket-Status: {showPackageStatus?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div>
                <span className="text-slate-500">Absender:</span>
                <p className="font-medium">{showPackageStatus?.sender_name}</p>
              </div>
              <div>
                <span className="text-slate-500">Empfänger:</span>
                <p className="font-medium">{showPackageStatus?.receiver_name}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                Pakete ({showPackageStatus?.packages?.length || 0})
              </h4>
              
              {showPackageStatus?.packages?.map((pkg, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3" data-testid={`package-status-item-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">Paket #{idx + 1}</span>
                      <span className="text-sm text-slate-500 ml-2">({pkg.category} - {pkg.weight}kg)</span>
                      {pkg.ident_number && (
                        <span className="text-xs text-slate-400 ml-2">ID: {pkg.ident_number}</span>
                      )}
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      pkg.status === "delivered" ? "bg-green-100 text-green-700" :
                      pkg.status === "picked_up" ? "bg-blue-100 text-blue-700" :
                      pkg.status === "error" ? "bg-red-100 text-red-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {pkg.status === "delivered" ? "Zugestellt" :
                       pkg.status === "picked_up" ? "Abgeholt" :
                       pkg.status === "error" ? "Fehler" :
                       "Ausstehend"}
                    </div>
                  </div>
                  
                  {pkg.status === "error" && pkg.error_type && (
                    <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      <strong>Fehler:</strong> {
                        pkg.error_type === "not_available" ? "Paket nicht verfügbar" :
                        pkg.error_type === "misrouting" ? "Fehlrouting" :
                        pkg.error_type === "damaged" ? "Beschädigt" :
                        pkg.error_type === "other" ? "Sonstiges" : pkg.error_type
                      }
                      {pkg.error_info && <span className="block mt-1">{pkg.error_info}</span>}
                    </div>
                  )}
                  
                  {pkg.status_updated_by && (
                    <p className="text-xs text-slate-400">
                      Zuletzt aktualisiert von {pkg.status_updated_by}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <Button 
                      size="sm" 
                      variant={pkg.status === "pending" ? "default" : "outline"}
                      className={pkg.status === "pending" ? "bg-slate-600" : ""}
                      onClick={() => handleUpdatePackageStatus(showPackageStatus.id, idx, "pending")}
                    >
                      Ausstehend
                    </Button>
                    <Button 
                      size="sm" 
                      variant={pkg.status === "picked_up" ? "default" : "outline"}
                      className={pkg.status === "picked_up" ? "bg-blue-600" : ""}
                      onClick={() => handleUpdatePackageStatus(showPackageStatus.id, idx, "picked_up")}
                    >
                      Abgeholt
                    </Button>
                    <Button 
                      size="sm" 
                      variant={pkg.status === "delivered" ? "default" : "outline"}
                      className={pkg.status === "delivered" ? "bg-green-600" : ""}
                      onClick={() => handleUpdatePackageStatus(showPackageStatus.id, idx, "delivered")}
                    >
                      Zugestellt
                    </Button>
                    <Select 
                      value={pkg.status === "error" ? pkg.error_type || "not_available" : ""}
                      onValueChange={(errorType) => {
                        const errorInfo = prompt("Zusätzliche Info zum Fehler (optional):");
                        handleUpdatePackageStatus(showPackageStatus.id, idx, "error", errorType, errorInfo);
                      }}
                    >
                      <SelectTrigger className={`w-32 ${pkg.status === "error" ? "bg-red-600 text-white border-red-600" : ""}`}>
                        <SelectValue placeholder="Fehler..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_available">Nicht verfügbar</SelectItem>
                        <SelectItem value="misrouting">Fehlrouting</SelectItem>
                        <SelectItem value="damaged">Beschädigt</SelectItem>
                        <SelectItem value="other">Sonstiges</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <h5 className="font-medium text-slate-900 mb-2">Status-Legende:</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-slate-400"></div>
                  <span>Ausstehend - Wartet auf Bearbeitung</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-blue-500"></div>
                  <span>Abgeholt - Wurde abgeholt</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-green-500"></div>
                  <span>Zugestellt - Beim Empfänger</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-red-500"></div>
                  <span>Fehler - Problem aufgetreten</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackageStatus(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Details Modal */}
      <Dialog open={!!showInvoiceDetails} onOpenChange={() => { setShowInvoiceDetails(null); setInvoiceDetailData(null); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Rechnung: {showInvoiceDetails?.month_display} - {showInvoiceDetails?.customer_name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingInvoiceDetail ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : invoiceDetailData && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Kunde</p>
                    <p className="font-semibold">{invoiceDetailData.customer?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Firma</p>
                    <p className="font-semibold">{invoiceDetailData.customer?.company || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">E-Mail</p>
                    <p className="font-semibold">{invoiceDetailData.customer?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Zeitraum</p>
                    <p className="font-semibold">{showInvoiceDetails?.month_display}</p>
                  </div>
                </div>
              </div>
              
              {/* Orders Table */}
              <div>
                <h4 className="font-semibold mb-3">Sendungen ({invoiceDetailData.orders?.length || 0})</h4>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Sendungsnr.</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Datum</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Empfänger</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Stadt</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">Pakete</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Netto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {invoiceDetailData.orders?.map((order, idx) => (
                        <tr key={order.order_number || idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium">{order.order_number}</td>
                          <td className="px-3 py-2 text-slate-600">{order.date}</td>
                          <td className="px-3 py-2 text-slate-600">{order.receiver}</td>
                          <td className="px-3 py-2 text-slate-600">{order.receiver_city}</td>
                          <td className="px-3 py-2 text-center text-slate-600">{order.packages}</td>
                          <td className="px-3 py-2 text-right font-medium">{order.price?.toFixed(2)}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Totals */}
              <div className="border-t pt-4">
                <div className="space-y-2 max-w-xs ml-auto">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Netto:</span>
                    <span className="font-medium">{invoiceDetailData.net_total?.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">MwSt. (19%):</span>
                    <span className="font-medium">{invoiceDetailData.tax_amount?.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Brutto:</span>
                    <span className="text-green-600">{invoiceDetailData.total?.toFixed(2)}€</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInvoiceDetails(null); setInvoiceDetailData(null); }}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
